import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import { Button, Grid } from '@material-ui/core';
import { Visibility } from '@material-ui/icons';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import HoveredCard from '../../common/card/HoveredCard';
import DeleteEntityCard from '../../common/cards/DeleteEntityCard';
import CartIconBadge from '../../common/cart/CartIconBadge';
import { formatDate } from '../../common/formatter';
import HeaderBackButton, {
  StyledPageTemplate
} from '../../common/HeaderBackButton';
import HeaderTitle from '../../common/HeaderTitle';
import PageTemplateAlert from '../../common/page-template/PageTemplateAlert';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import environment from '../../environments';
import { useUserContext } from '../../user/User.context';
import { useJiraConnectAPI } from '../jira-connect/JiraConnect.hooks';
import { JiraServerContext } from '../jira-server/jira-server.context';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import TaskAssignCard from '../tasks/cards/TaskAssignCard';
import TaskAttachmentCard from '../tasks/cards/TaskAttachmentCard';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import TaskStatusVersionCard from '../tasks/cards/TaskStatusVersionCard';
import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import { ProductBoardColumnEntity } from './board/ProductBoardColumn';
import DesignInputDesignOutputReferenceCard from './cards/DesignInputDesignOutputReferenceCard';
import DesignInputInfoCard from './cards/DesignInputInfoCard';
import DesignInputUserNeedReferenceCard from './cards/DesignInputUserNeedReferenceCard';
import DesignInputValidationReferenceCard from './cards/DesignInputValidationReferenceCard';
import TaskJiraIssueAssignCard from '../tasks/cards/TaskJiraIssueAssignCard';
import ProductsSubSectionComponentCard from './cards/ProductsSubSectionComponentCard';
import ProductsSubSectionReleaseCard from './cards/ProductsSubSectionReleaseCard';
import {
  getProductEntityName,
  isProductRelationDisabled
} from './Product.config';
import {
  useFindProductDesignInputById,
  useFindProductDesignInputVersion,
  useProductEntityPrefixId
} from './Product.hooks';
import {
  BaseProductDesignInputCardProps,
  ProductDesignInput
} from './Product.interface';
import TaskDueDateCard from '../tasks/cards/TaskDueDateCard';
import DesignInputVerificationReferenceCard from './cards/DesignInputVerificationReferenceCard';
import DesignInputDesignSpecReferenceCard from './cards/DesignInputDesignSpecReferenceCard';

const ProductDesignInputDetailPage: React.FC = () => {
  const {
    productId: paramProductId = '',
    designInputId: paramDesignInputId = ''
  } = useParams<{
    productId: string;
    designInputId: string;
  }>();
  const productId: number = parseInt(paramProductId) || 0;
  const designInputId: number = parseInt(paramDesignInputId) || 0;
  const { hasRole } = useUserContext();
  const [from = 'detail'] = useQueryParam('from', StringParam);
  const [version] = useQueryParam('ver', withDefault(NumberParam, undefined));
  const [productDesignInput, setProductDesignInput] = useState<
    ProductDesignInput | undefined
  >();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const { isAvailable: isJiraConnect } = useJiraConnectAPI();
  const { isJiraServer } = useContext(JiraServerContext);
  const {
    productDesignInput: data,
    isLoadingProductDesignInput,
    findProductDesignInputError,
    findProductDesignInput
  } = useFindProductDesignInputById(productId, designInputId);
  const {
    productDesignInputVersion: loadedProductDesignInputVersion,
    findProductDesignInputVersion
  } = useFindProductDesignInputVersion(productId, designInputId, version);
  const [productDesignInputVersion, setProductDesignInputVersion] = useState(
    loadedProductDesignInputVersion
  );

  const title: string = useProductEntityPrefixId(
    productDesignInput,
    ProductBoardColumnEntity.DesignInput
  );

  const cardProps: BaseProductDesignInputCardProps = {
    productId,
    productDesignInput: productDesignInput!,
    productDesignInputVersion,
    onChange: (newProductDesignInput: Partial<ProductDesignInput>) => {
      if (productDesignInput) {
        // notify task components to refresh
        productDesignInput.task = { ...productDesignInput.task };
        setProductDesignInput({
          ...productDesignInput,
          ...newProductDesignInput
        });
      }
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleTaskUpdate = (newTask: Partial<Task>) => {
    if (!productDesignInput?.task) {
      return;
    }
    Object.assign(productDesignInput.task, newTask);
    cardProps.onChange?.({});
    cardProps.onUpdate?.();
  };

  useEffect(() => {
    findProductDesignInput();
  }, [findProductDesignInput, productId, designInputId]);
  useEffect(() => {
    if (version) {
      findProductDesignInputVersion();
    } else {
      setProductDesignInputVersion(undefined);
    }
  }, [findProductDesignInputVersion, productId, designInputId, version]);
  useEffect(
    () =>
      loadedProductDesignInputVersion &&
      setProductDesignInputVersion(loadedProductDesignInputVersion),
    [loadedProductDesignInputVersion]
  );

  useEffect(() => {
    data && setProductDesignInput(data);
  }, [data]);

  useEffect(() => {
    setError(findProductDesignInputError);
  }, [findProductDesignInputError]);
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.DesignInput>(
    productDesignInput?.task
  );
  const task = productDesignInputVersion?.task || productDesignInput?.task;
  return (
    <StyledPageTemplate>
      <PageTemplateHeader>
        <HeaderBackButton to={`/portal/products/${productId}/${from}`} />
        <HeaderTitle>
          <h2>{isLoadingProductDesignInput ? 'Loading...' : title}</h2>
          {productDesignInput && (
            <div className='status-container'>
              <TaskStatusLabel task={productDesignInput.task} />
            </div>
          )}
        </HeaderTitle>
        <PageTemplateHeaderActions flex>
          <CartIconBadge
            taskEntity={productDesignInput}
            onChange={productDesignInput =>
              setProductDesignInput(productDesignInput as ProductDesignInput)
            }
          />
          {productDesignInput?.task && (
            <TaskStatusActions
              task={productDesignInput.task}
              taskEntity={productDesignInput}
              onChange={(task: Task) => {
                productDesignInput.task = task;
                setProductDesignInput({ ...productDesignInput });
              }}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      {productDesignInput && version && (
        <PageTemplateAlert severity='warning'>
          Viewing previous version {version}
          {productDesignInputVersion &&
            ` from ${formatDate(productDesignInputVersion.created)}. `}
          <Link to={`./${productDesignInput.id}`}>
            Click here to view latest version
          </Link>
        </PageTemplateAlert>
      )}
      <PageTemplateDashboard>
        {productDesignInput && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <DesignInputInfoCard {...cardProps} />
                </Grid>
                {(isJiraConnect || isJiraServer) && (
                  <Grid item xs={12}>
                    <TaskJiraIssueAssignCard
                      task={productDesignInput.task}
                      entity={{
                        title: productDesignInput.title,
                        description: productDesignInput.description
                      }}
                      jiraFieldColumns={
                        environment.productConfig?.designInput
                          ?.visibleJiraFields
                      }
                      onChange={taskJiraIssues => {
                        // update productDesignInput Task jira issues on change
                        productDesignInput.task.jiraIssues = taskJiraIssues;
                        setProductDesignInput({ ...productDesignInput });
                      }}
                    />
                  </Grid>
                )}
                {productDesignInput.task && (
                  <Grid item xs={12}>
                    <TaskAttachmentCard
                      task={productDesignInput.task}
                      disabled={Boolean(productDesignInputVersion)}
                      attachments={productDesignInputVersion?.attachments}
                      onError={cardProps.onError}
                      onChange={handleTaskUpdate}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3}>
                {productDesignInput.task && (
                  <>
                    <Grid item xs={12}>
                      <TaskStatusVersionCard
                        showCart
                        taskEntity={productDesignInput}
                        renderContent={group =>
                          group.version !== productDesignInput.task.version && (
                            <HoveredCard
                              title={`Version ${group.version}`}
                              content='View the previous version'
                              actions={
                                <Button
                                  variant='text'
                                  color='primary'
                                  component={Link}
                                  startIcon={<Visibility />}
                                  to={`./${productDesignInput.id}?ver=${group.version}`}
                                >
                                  View
                                </Button>
                              }
                            />
                          )
                        }
                      />
                    </Grid>
                    {task?.children && task.children.length > 0 && (
                      <Grid item xs={12}>
                        <SubTaskListCard
                          title='Stakeholders'
                          task={task}
                          disabled={
                            Boolean(version) ||
                            isFieldDisabled('assignSubTasks')
                          }
                          onError={cardProps.onError}
                          onChange={handleTaskUpdate}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <ProductsSubSectionComponentCard
                        productSubSection={productDesignInput}
                        productId={productId}
                        readOnlyComponents={
                          productDesignInputVersion &&
                          (productDesignInputVersion.components || [])
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ProductsSubSectionReleaseCard
                        productId={productId}
                        productSubSection={productDesignInput}
                        disabled={Boolean(productDesignInputVersion)}
                        readOnlyRelease={
                          productDesignInputVersion &&
                          (productDesignInputVersion?.release || null)
                        }
                      />
                    </Grid>
                    {task && (
                      <Grid item xs={12}>
                        <TaskAssignCard task={task} />
                      </Grid>
                    )}
                    {productDesignInput.task && (
                      <>
                        <Grid item xs={12}>
                          <TaskFileListCard
                            task={productDesignInput.task}
                            disabled={Boolean(productDesignInputVersion)}
                            onError={cardProps.onError}
                            onChange={handleTaskUpdate}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TaskDueDateCard
                            task={productDesignInput.task}
                            disabled={Boolean(productDesignInputVersion)}
                            onError={cardProps.onError}
                            onChange={handleTaskUpdate}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
                {!isProductRelationDisabled('designInput', 'userNeeds') && (
                  <Grid item xs={12}>
                    <DesignInputUserNeedReferenceCard {...cardProps} />
                  </Grid>
                )}
                {!isProductRelationDisabled('designInput', 'designOutputs') && (
                  <Grid item xs={12}>
                    <DesignInputDesignOutputReferenceCard {...cardProps} />
                  </Grid>
                )}
                {!isProductRelationDisabled('designInput', 'verifications') && (
                  <Grid item xs={12}>
                    <DesignInputVerificationReferenceCard {...cardProps} />
                  </Grid>
                )}
                {!isProductRelationDisabled('designInput', 'validations') && (
                  <Grid item xs={12}>
                    <DesignInputValidationReferenceCard {...cardProps} />
                  </Grid>
                )}
                {!isProductRelationDisabled('designInput', 'designSpecs') && (
                  <Grid item xs={12}>
                    <DesignInputDesignSpecReferenceCard {...cardProps} />
                  </Grid>
                )}
                {hasRole('Delete') && (
                  <Grid item xs={12}>
                    <DeleteEntityCard
                      title={getProductEntityName('designInput')}
                      apiPath={`products/${productId}/design-inputs`}
                      entity={productDesignInput}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </PageTemplateDashboard>

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Product {getProductEntityName('designInput')} details updated.
      </AppSnackbar>
    </StyledPageTemplate>
  );
};

export default ProductDesignInputDetailPage;
